<template>
  <LayoutContainer class="max-w-4xl">
    <LayoutH1>{{ t('pages.org_contact.title') }}</LayoutH1>

    <LayoutP class="mb-2 text-xl">
      {{ t('pages.org_contact.subTitle', { name: user.first_name || '' }) }}
    </LayoutP>

    <LayoutP>{{ t('pages.org_contact.description') }}</LayoutP>

    <div class="flex flex-col gap-6 mt-6">
      <ActionWriteGuide />

      <UAlert
        color="neutral"
        icon="i-lucide-triangle-alert"
        variant="subtle"
        :ui="{
          title: 'text-lg font-semibold',
          description: 'text-md font-medium',
        }"
        :title="t('pages.org_contact.warning.title')"
        :description="t('pages.org_contact.warning.description')"
      />

      <ActionCounterContact class="font-semibold text-lg" />

      <UCard
        v-for="{ id, name, logo, description, email, social_media, phone, postcard } in organisations"
        :key="id"
        :ui="{
          root: 'max-xs:px-1 xs:px-4 py-4 bg-neutral-50',
          header: 'flex flex-col gap-2 items-center justify-center p-0 sm:p-0 !py-4',
          body: 'flex flex-row flex-wrap justify-between items-center gap-4 p-0 sm:p-0 !py-4',
        }"
      >
        <template #header>
          <img
            class="w-full max-w-[100px] h-auto"
            :src="`/img/organisations/${logo.file_name}`"
            :alt="`${name} logo`"
            :title="name"
            :width="logo.width"
            :height="logo.height"
          >
          <UModal
            v-model:open="is_open[id]"
            :title="t('pages.org_contact.whyCompanyModal.title', { company: name })"
            :description="t('pages.org_contact.whyCompanyModal.description')"
          >
            <UButton
              variant="link"
              size="md"
              color="neutral"
              class="cursor-pointer"
              :label="t('pages.org_contact.whyCompany', { company: name })"
            />
            <template #body>
              <p class="text-lg">
                {{ description }}
              </p>
            </template>
            <template #footer>
              <UButton
                :label="t('pages.org_contact.whyCompanyModal.actionButton')"
                class="mx-auto"
                size="xl"
                @click="is_open[id] = false"
              />
            </template>
          </UModal>
        </template>
        <div
          v-if="email && email.active"
          class="flex flex-col justify-start items-start gap-4 bg-white max-md:p-2 md:p-4 border rounded-sm w-full"
        >
          <h3
            class="w-full font-semibold text-xl"
            v-text="t('pages.org_contact.sendEmail.title')"
          />

          <div class="flex flex-col gap-8">
            <ActionEmail
              v-if="email.address"
              :id="id"
              :to="email.address"
              size="md"
              :bcc="`${user.auth_id}.contact.org.${id}@${action_email_domain}`"
              orientation="horizontal"
              :target-name="name"
              :email-templates="email.templates"
              @link-clicked="openConfirmModal(id, name, 'email', '')"
            />

            <UButton
              v-else-if="email.contact_form_url"
              color="neutral"
              size="lg"
              :label="t('pages.org_contact.sendEmail.btnContactForm')"
              :to="email.contact_form_url"
              :external="true"
              target="_blank"
              rel="noopener noreferrer"
              @click="openConfirmModal(id, name, 'email', '')"
            />

            <USwitch
              v-model="progress.email[id]"
              size="xl"
              checked-icon="i-lucide-check"
              unchecked-icon="i-lucide-x"
              :loading="loading.email[id]"
              style="cursor: pointer;"
              :label="t('pages.org_contact.save_progress')"
              :description="t('pages.org_contact.sendEmail.are_you_done', { company: name })"
              @change="saveProgress('email', id)"
            />
          </div>
        </div>
        <div
          v-if="social_media && social_media.active"
          class="flex flex-col justify-start items-start gap-4 bg-white max-md:p-2 md:p-4 border rounded-sm w-full"
        >
          <h3
            class="w-full font-semibold text-xl"
            v-text="t('pages.org_contact.socialMedia.title')"
          />

          <div class="flex flex-col items-start gap-8">
            <ActionSocialMedia
              :social-media="social_media.platforms"
              size="xl"
              @btn-click="(platform: string) => openConfirmModal(id, name, 'some', platform)"
            />

            <USwitch
              v-model="progress.some[id]"
              size="xl"
              checked-icon="i-lucide-check"
              unchecked-icon="i-lucide-x"
              style="cursor: pointer;"
              :label="t('pages.org_contact.save_progress')"
              :description="t('pages.org_contact.socialMedia.are_you_done', { company: name })"
              :loading="loading.some[id]"
              @change="saveProgress('some', id)"
            />
          </div>
        </div>
        <div
          v-if="phone && phone.active"
          class="flex flex-col justify-start items-start gap-4 bg-white max-md:p-2 md:p-4 border rounded-sm w-full"
        >
          <h3
            class="w-full font-semibold text-xl"
            v-text="t('pages.org_contact.phone.title')"
          />

          <div class="flex flex-col items-start gap-8">
            <div class="flex flex-row flex-wrap items-center gap-4">
              <UButton
                color="neutral"
                size="lg"
                icon="i-lucide-phone"
                variant="subtle"
                :label="t('pages.org_contact.phone.btn_call')"
                :to="`tel:${phone.number}`"
                @click="openConfirmModal(id, name, 'phone', '')"
              />

              <p class="text-xl">
                {{ parsePhoneNumber(phone.number)?.formatNational() }}
              </p>
            </div>

            <UModal
              v-if="phone.template"
              :title="t('pages.org_contact.phone.script_title')"
              :description="t('pages.org_contact.phone.script_description')"
            >
              <UButton
                color="neutral"
                variant="subtle"
                size="md"
                icon="i-lucide-list"
                class="inline-flex w-auto"
                :label="t('pages.org_contact.phone.btn_script')"
              />
              <template #body>
                <ul class="flex flex-col gap-2 list-disc list-inside">
                  <li
                    v-for="(line, index) in phone.template"
                    :key="index"
                    v-text="addInfo(line, user, name)"
                  />
                </ul>
              </template>
            </UModal>

            <USwitch
              v-model="progress.phone[id]"
              size="xl"
              checked-icon="i-lucide-check"
              unchecked-icon="i-lucide-x"
              style="cursor: pointer;"
              :label="t('pages.org_contact.save_progress')"
              :description="t('pages.org_contact.phone.are_you_done', { company: name })"
              :loading="loading.phone[id]"
              @change="saveProgress('phone', id)"
            />
          </div>
        </div>
        <div
          v-if="postcard && postcard.active"
          class="flex flex-col justify-start items-start gap-4 bg-white p-4 border rounded-sm w-full"
        >
          <h3
            class="w-full font-semibold text-xl"
            v-text="t('pages.org_contact.postcard.title')"
          />

          <div class="flex flex-col gap-8">
            <div class="flex flex-row flex-wrap items-center gap-4">
              {{ t('pages.org_contact.postcard.description', { company: name, address: postcard.address }) }}
            </div>

            <USwitch
              v-model="progress.postcard[id]"
              size="xl"
              checked-icon="i-lucide-check"
              unchecked-icon="i-lucide-x"
              style="cursor: pointer;"
              :label="t('pages.org_contact.save_progress')"
              :description="t('pages.org_contact.postcard.are_you_done', { company: name })"
              :loading="loading.postcard[id]"
              @change="saveProgress('postcard', id)"
            />
          </div>
        </div>
      </ucard>
    </div>
    <ActionModalConfirm
      v-model:open="confirm_modal_open"
      action-type="contact"
      :contact-name="current_name"
      :is-loading="confirm_modal_loading || false"
      :action-platform="current_action_platform"
      @close="confirm_modal_open = false"
      @confirm="saveProgress(current_action_platform, current_id, true)"
    />

    <div class="flex flex-col items-center gap-6 mt-10 mb-20">
      <UButton
        :label="is_loading ? t('shared.wait') : t('pages.org_contact.btnDone')"
        size="xl"
        :loading="is_loading"
        @click="onNext()"
      />
      <UButton
        :label="t('pages.org_contact.btnLater')"
        :padded="false"
        variant="link"
        color="inherit"
        size="md"
        @click="onLater()"
      />
    </div>
  </LayoutContainer>
</template>

<script setup lang="ts">
import parsePhoneNumber from 'libphonenumber-js'
import { UModal } from '#components'

const { organisations, data: { action_email_domain } } = getData()
const is_loading = ref(false)
const user = useUserStore()
const { event } = useTracker()

const is_open = ref<Record<string, boolean>>({})

interface Progress {
  email: Record<string, boolean>
  some: Record<string, boolean>
  phone: Record<string, boolean>
  postcard: Record<string, boolean>
}

type ActionPlatforms = 'email' | 'some' | 'phone' | 'postcard'

const progress = ref<Progress>({
  email: {},
  some: {},
  phone: {},
  postcard: {},
})
const loading = ref<Progress>({
  email: {},
  some: {},
  phone: {},
  postcard: {},
})

onMounted(() => {
  const selected_email = stringListToArray(user.org_email)
  const selected_some = stringListToArray(user.org_some)
  const selected_phone = stringListToArray(user.org_phone)
  const selected_postcard = stringListToArray(user.org_postcard)
  selected_email.forEach((id) => {
    progress.value.email[id] = true
  })
  selected_some.forEach((id) => {
    progress.value.some[id] = true
  })
  selected_phone.forEach((id) => {
    progress.value.phone[id] = true
  })
  selected_postcard.forEach((id) => {
    progress.value.postcard[id] = true
  })
})

async function saveProgress(type: ActionPlatforms, id: string, manual_new_value?: boolean) {
  if (typeof manual_new_value === 'boolean') {
    progress.value[type][id] = manual_new_value
  }

  confirm_modal_open.value = false
  loading.value[type][id] = true
  const new_value: boolean = progress.value[type][id]!

  try {
    if (type === 'email') {
      await user.saveAction({
        target_type: 'org',
        target_id: id,
        target_platform: current_target_platform.value,
        action_type: 'contact',
        action_platform: 'email',
        add_action: new_value,
        org_email: user.updateList('org_email', id, new_value),
      })
    }
    else if (type === 'some') {
      await user.saveAction({
        target_type: 'org',
        target_id: id,
        target_platform: current_target_platform.value,
        action_type: 'contact',
        action_platform: 'some',
        add_action: new_value,
        org_some: user.updateList('org_some', id, new_value),
      })
    }
    else if (type === 'phone') {
      await user.saveAction({
        target_type: 'org',
        target_id: id,
        target_platform: current_target_platform.value,
        action_type: 'contact',
        action_platform: 'phone',
        add_action: new_value,
        org_phone: user.updateList('org_phone', id, new_value),
      })
    }
    else if (type === 'postcard') {
      await user.saveAction({
        target_type: 'org',
        target_id: id,
        target_platform: current_target_platform.value,
        action_type: 'contact',
        action_platform: 'postcard',
        add_action: new_value,
        org_postcard: user.updateList('org_postcard', id, new_value),
      })
    }
    else {
      console.error('Unsupported type', type)
    }

    event({
      google: `contact_${type}`,
      meta: `contact_${type}`,
      snapchat: 'CUSTOM_EVENT_5',
    })
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  catch (error) {
    progress.value[type][id] = !new_value
  }
  finally {
    loading.value[type][id] = false
  }
}

const current_id = ref<string>('')
const current_name = ref<string>('')
const current_target_platform = ref<string>('')
const current_action_platform = ref<ActionPlatforms>('email')
const confirm_modal_open = ref(false)
const confirm_modal_loading = computed(() => {
  if (!current_id.value) {
    return false
  }
  return loading.value.email[current_id.value] || loading.value.some[current_id.value]
})

async function openConfirmModal(id: string, name: string, action_platform: ActionPlatforms, target_platform: string) {
  log('openConfirmModal', id, name, action_platform, target_platform)
  confirm_modal_open.value = true
  current_name.value = name
  current_id.value = id
  current_action_platform.value = action_platform
  current_target_platform.value = target_platform
}

function onNext() {
  event({ google: 'contact_done' })
  navigateTo(user.next_page)
}

function onLater() {
  event({ google: 'contact_later' })
  navigateTo(user.next_page)
}
</script>
